import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import type { HumanName } from '@medplum/fhirtypes'

dayjs.extend(utc)
dayjs.extend(timezone)

// Clinic display zone: clinical dates are both rendered AND parsed here, never in the browser's zone.
const CLINIC_TZ = 'America/New_York'

export function formatDateTime(
  dateTime: string,
  _delimeter: string = '/',
  includeTime: boolean = false,
  skipTimezone: boolean = false,
): string {
  if (!dateTime) return dateTime

  const format = includeTime ? 'MM/DD/YYYY hh:mm A' : 'MM/DD/YYYY'

  if (skipTimezone) {
    return dayjs(dateTime).format(format)
  }

  return dayjs(dateTime).tz(CLINIC_TZ).format(format)
}

// PD-1897: "on 07/14/2025 at 3:31 PM EDT" — clinic-tz like formatDateTime, plus the zone's short name.
export function formatSignatureTime(dateTime: string): string {
  const instant = dayjs(dateTime).tz(CLINIC_TZ)
  const zone =
    new Intl.DateTimeFormat('en-US', { timeZone: CLINIC_TZ, timeZoneName: 'short' })
      .formatToParts(new Date(dateTime))
      .find((part) => part.type === 'timeZoneName')?.value ?? 'ET'
  return `on ${instant.format('MM/DD/YYYY')} at ${instant.format('h:mm A')} ${zone}`
}

export function formatSignatureLine(signerName: string, signedAt: string): string {
  return `Electronically signed by ${signerName} ${formatSignatureTime(signedAt)}.`
}

// Left-rail rows: same-day → "Today, 8:54 PM", else date — "Today" in clinic-tz to match formatDateTime.
export function formatListDate(dateTime: string): string {
  if (!dateTime) return dateTime

  const target = dayjs(dateTime).tz(CLINIC_TZ)

  return target.isSame(dayjs().tz(CLINIC_TZ), 'day')
    ? `Today, ${target.format('h:mm A')}`
    : formatDateTime(dateTime)
}

// UTC ISO → clinic-tz `YYYY-MM-DDTHH:mm` for a datetime-local input; inverse of fromClinicDateTimeLocal.
export function toClinicDateTimeLocal(value?: string): string {
  if (!value) return ''
  const parsed = dayjs.utc(value)
  return parsed.isValid() ? parsed.tz(CLINIC_TZ).format('YYYY-MM-DDTHH:mm') : ''
}

// Clinic-tz datetime-local → UTC ISO; a value already carrying a zone is passed through unchanged.
export function fromClinicDateTimeLocal(value?: string): string | undefined {
  if (!value) return undefined
  if (/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value)) return value
  const parsed = dayjs.tz(value, CLINIC_TZ)
  return parsed.isValid() ? parsed.toISOString() : value
}

export function calculateAge(birthDate: string): string {
  const today = new Date()
  const birthDateObj = new Date(birthDate)
  let age = today.getFullYear() - birthDateObj.getFullYear()
  const m = today.getMonth() - birthDateObj.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birthDateObj.getDate())) {
    age--
  }
  return age.toString()
}

export function formatPatientName(name?: HumanName, familyFirst = false): string {
  if (!name) return ''
  const family = name.family || ''
  const given = name.given?.join(' ') || ''
  if (!family && !given) return name.text || ''
  if (!given) return family
  if (!family) return given
  return familyFirst ? `${family}, ${given}` : `${given} ${family}`
}

export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '')
  // A leading US country code, with or without the '+': "+1…" (E.164) or "1…".
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (local.length !== 10) return value
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`
}

// Progressive input mask for a phone field, mirroring patient-web registration
// (AccountStep). A US number is shown as "(818) 280-7465" while typing; a value
// that starts with "+" is treated as international and passed through untouched
// (e.g. a Spanish "+34…" is never coerced into US format).
export function formatPhoneInput(raw: string): string {
  if (raw.trimStart().startsWith('+')) {
    return `+${raw.replace(/\D/g, '').slice(0, 15)}`
  }
  let digits = raw.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
  digits = digits.slice(0, 10)
  if (digits.length > 6) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  if (digits.length > 3) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  if (digits.length > 0) return `(${digits}`
  return ''
}

// True when a phone value is complete enough to send: a full international
// number (starts with "+") or at least 10 local (US) digits.
export function isPhoneReady(phone: string): boolean {
  if (phone.startsWith('+')) {
    return /^\+[1-9]\d{7,14}$/.test(phone)
  }
  return phone.replace(/\D/g, '').length >= 10
}

// Normalize to E.164 for delivery. A value that already starts with "+" keeps
// its country code (international numbers are preserved); a bare 10-digit number
// is assumed US and gets a "+1" prefix.
export function normalizePhoneToE164(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  if (phone.startsWith('+')) return `+${digits}`
  let normalizedDigits = digits
  if (normalizedDigits.length === 10) normalizedDigits = `1${normalizedDigits}`
  return `+${normalizedDigits}`
}

export const SNOMED_USER_ROLES = {
  CC_MANAGER: {
    url: 'http://hl7.org.au/fhir/ValueSet/snomed-practitioner-role',
    code: '224565004',
    name: 'Call center manager',
  },
  MEDICAL_DOCTOR: {
    url: 'http://hl7.org.au/fhir/ValueSet/snomed-practitioner-role',
    code: '112247003',
    name: 'Medical doctor',
  },
  HOSPITAL_MANAGER: {
    url: 'http://hl7.org.au/fhir/ValueSet/snomed-practitioner-role',
    code: '303129000',
    name: 'Hospital manager',
  },
}
